import { useOrientationStore } from '@/stores/orientation.store'
import { orientationAPI } from '@/lib/orientationApi'
import type { OrientationRequest } from '@/lib/orientationApi'
import OrientationForm from '@/components/orientation/OrientationForm'
import OrientationResults from '@/components/orientation/OrientationResults'
import ConfirmationCard from '@/components/orientation/ConfirmationCard'
import toast from 'react-hot-toast'

const STEPS = [
  { num: 1, label: 'Saisie' },
  { num: 2, label: 'Recommandations' },
  { num: 3, label: 'Confirmation' },
]

export default function OrientationPage() {
  const { step, setResult, setConfirmResult, setLoading, setError, reset, isLoading } = useOrientationStore()

  const handleSubmit = async (data: OrientationRequest) => {
    setLoading(true)
    setError(null)
    try {
      const result = await orientationAPI.create(data)
      setResult(result)
      toast.success(`${result.recommendations.length} structure(s) recommandee(s)`)
    } catch (err: any) {
      const message = err?.message || 'Erreur lors du scoring'
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async (structureId: string) => {
    const { result } = useOrientationStore.getState()
    if (!result) return

    setLoading(true)
    try {
      const confirmResult = await orientationAPI.confirm(result.orientationId, {
        chosenStructureId: structureId,
      })
      setConfirmResult(confirmResult)
      toast.success('Orientation confirmee')
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la confirmation')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-neutral-900">Orientation du patient</h1>
          <p className="text-sm text-neutral-500">Trouvez la structure la plus adaptee</p>
        </div>
        {step > 1 && (
          <button
            onClick={reset}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Nouvelle orientation
          </button>
        )}
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= s.num
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-400'
            }`}>
              {step > s.num ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                s.num
              )}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-neutral-700 font-medium' : 'text-neutral-400'}`}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div className={`w-8 h-0.5 ${step > s.num ? 'bg-primary-600' : 'bg-neutral-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <OrientationForm onSubmit={handleSubmit} />
        </div>
      )}

      {step === 2 && (
        <OrientationResults onConfirm={handleConfirm} isConfirming={isLoading} />
      )}

      {step === 3 && <ConfirmationCard />}
    </div>
  )
}
